import React, { useState, useRef, useEffect } from "react";
import Onboarding from "../onboarding/Onboarding";
import ManagerDashboard from "./ManagerDashboard";
import UserDetails from "./UserDetails";
import { useNavigate, useLocation } from "react-router-dom";
import KanbanBoard from "./KanbanBoard";
import DailyTasks from "./DailyTasks";
import "./Dashboard.css";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  LineChart,
  Line,
} from "recharts";
import { FiPlus, FiBell } from "react-icons/fi";
import useNotificationPoll from "../../hooks/useNotificationPoll";
import { useNotifications } from "../../hooks/useNotification";
// Sample data for Sales Insights
const pieData = [
  { name: "Closed", value: 0 },
  { name: "In Progress", value: 0 },
  { name: "Lost", value: 0 },
];
const COLORS = ["#1976d2", "#43cea2", "#b0b7c3"];
const barData = [
  { name: "Jan", Deals: 0 },
  { name: "Feb", Deals: 0 },
  { name: "Mar", Deals: 0 },
  { name: "Apr", Deals: 0 },
  { name: "May", Deals: 0 },
  { name: "Jun", Deals: 0 },
];
const lineData = [
  { name: "Jan", Conversion: 0 },
  { name: "Feb", Conversion: 0 },
  { name: "Mar", Conversion: 0 },
  { name: "Apr", Conversion: 0 },
  { name: "May", Conversion: 0 },
  { name: "Jun", Conversion: 0 },
];
function SalesInsights() {
  return (
    <div style={{ padding: 0 }}>
      <div style={{ fontWeight: 700, fontSize: "1.125rem", marginBottom: 16, color: "#1a1a1a" }}>
        Sales Insights
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 24 }}>
        {/* Pie Chart */}
        <div style={{ flex: 1, minWidth: 180, height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) =>
                  `${name}: ${(percent * 100).toFixed(0)}%`
                }
                outerRadius={70}
                fill="#8884d8"
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
        {/* Bar Chart */}
        <div style={{ flex: 2, minWidth: 220, height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={barData}
              margin={{ top: 16, right: 16, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="Deals" fill="#1976d2" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
function Conversion() {
  return (
    <div style={{ padding: 0 }}>

      <div style={{ fontWeight: 700, fontSize: "1.125rem", marginBottom: 16, color: "#1a1a1a" }}>
        Conversion Insights
      </div>
      <div style={{ width: "100%", height: 220 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={lineData}
            margin={{ top: 16, right: 16, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="Conversion"
              stroke="#43cea2"
              strokeWidth={3}
              dot={{ r: 5 }}
              activeDot={{ r: 7 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
function TotalDeals() {
  return (
    <div style={{ padding: 0 }}>
      <div style={{ fontWeight: 700, fontSize: "1.125rem", marginBottom: 16, color: "#1a1a1a" }}>
        Total Deals
      </div>
      <div style={{ color: "#7b7b93" }}>Widget content here...</div>
    </div>
  );
}

const WIDGETS = [
  // { key: "dailyTasks", label: "Tasks", component: DailyTasks }, // Moved to dedicated Tasks page
  { key: "salesInsights", label: "Sales Insights", component: SalesInsights },
  { key: "conversion", label: "Conversion", component: Conversion },
  // { key: "totalDeals", label: "Total Deals", component: TotalDeals },
];

export default function Dashboard() {
  const [selectedWidgets, setSelectedWidgets] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  // If the page was fully reloaded, ignore any history location.state (browser may persist it)
  // and default to the pipeline tab. Otherwise prefer location.state.activeTab when present.
  const navEntries = (performance && performance.getEntriesByType)
    ? performance.getEntriesByType("navigation")
    : [];
  const navType = (navEntries && navEntries[0] && navEntries[0].type) || null;
  const isReload = navType === "reload";
  const initialActiveTab = (!isReload && location?.state && location.state.activeTab)
    ? location.state.activeTab
    : "manager";
  const [unreadCount, setUnreadCount] = useState(0);
  const [unread, setUnread] = useState(0);
  const [activeTab, setActiveTab] = useState(initialActiveTab); // "pipeline" or "manager"
  useEffect(() => {
    // Update tab when navigating with location.state, but ignore when the page was reloaded
    const navEntriesLocal = (performance && performance.getEntriesByType)
      ? performance.getEntriesByType("navigation")
      : [];
    const navTypeLocal = (navEntriesLocal && navEntriesLocal[0] && navEntriesLocal[0].type) || null;
    const reloaded = navTypeLocal === "reload";
    if (!reloaded && location && location.state && location.state.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state?.activeTab]);

  // Only show widgets that are not already added
  const availableWidgets = WIDGETS.filter(
    (w) => !selectedWidgets.includes(w.key)
  );

  const handleAddWidget = (key) => {
    setSelectedWidgets((prev) => [...prev, key]);
    setShowDropdown(false);
  };

  const handleRemoveWidget = (key) => {
    setSelectedWidgets((prev) => prev.filter((w) => w !== key));
  };

  React.useEffect(() => {
    function handleClickOutside(event) {
      if (showDropdown && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDropdown]);

  let user = localStorage.getItem('userData');
  // use polling hook to keep unread count updated across tabs
  // const { unreadCount: polledUnreadCount, refresh: refreshUnread } = useNotificationPoll({ intervalMs: 30 * 1000 });


  const d = useNotifications(user);
  useEffect(() => {
    setUnread(d.unreadCount);
    console.log(d.unreadCount);
  }, [d.unreadCount]);
  // // Sync polled value into local component state (so existing UI still uses unreadCount)
  // React.useEffect(() => {
  //   setUnreadCount(polledUnreadCount || 0);
  // }, [polledUnreadCount]);

  const AddWidgetBtn = () => (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        style={{
          background: "var(--primary-color)",
          color: "#fff",
          border: "none",
          borderRadius: 8,
          padding: "10px 20px",
          fontWeight: 600,
          fontSize: "0.9375rem",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 8,
          boxShadow: "0 2px 8px rgba(30,42,90,0.12)",
          transition: "all 0.2s ease",
          letterSpacing: 0.3,
        }}
        onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-1px)"}
        onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
      >
        <FiPlus style={{ fontSize: 18 }} /> Add Widget
      </button>
      {showDropdown && (
        <div
          ref={dropdownRef}
          style={{
            position: "absolute",
            top: 52,
            left: 0,
            background: "#fff",
            border: "1px solid #e0e0e0",
            borderRadius: 8,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            zIndex: 10,
            minWidth: 200,
            overflow: "hidden",
          }}
        >
          {availableWidgets.length === 0 ? (
            <div style={{ padding: "16px", color: "#888", fontSize: "0.875rem", textAlign: "center" }}>
              No more widgets to add
            </div>
          ) : (
            availableWidgets.map((widget, idx) => (
              <div
                key={widget.key}
                style={{
                  padding: "14px 18px",
                  cursor: "pointer",
                  borderBottom: idx < availableWidgets.length - 1 ? "1px solid #f0f0f0" : "none",
                  fontSize: "0.9375rem",
                  fontWeight: 500,
                  transition: "background 0.2s",
                }}
                onClick={() => handleAddWidget(widget.key)}
                onMouseEnter={(e) => e.currentTarget.style.background = "#f5f5f5"}
                onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
              >
                {widget.label}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );


  // Get user info for manager/member view and keep it in state so UI stays stable
  const parseUserData = () => {
    try {
      return JSON.parse(localStorage.getItem("userData") || "{}");
    } catch (e) {
      return {};
    }
  };

  const [userDataState, setUserDataState] = useState(() => parseUserData());
  const role = userDataState?.role;
  const userId = userDataState?.id;

  // Refresh userData when other tabs update localStorage or window regains focus
  React.useEffect(() => {
    const refresh = () => setUserDataState(parseUserData());
    const onStorage = (e) => {
      if (e.key === "userData") refresh();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) refresh();
    });

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, []);

  return (
    <div style={{ paddingBottom: 0, maxWidth: "1400px", margin: "0 auto", width: "100%", boxSizing: "border-box" }}>
      <div style={{ marginBottom: 32, position: "relative" }}>
        
        {/* Tab Switcher */}
        <div style={{ display: "flex", paddingTop: "12px", flexWrap: "wrap", gap: 0 }}>
          <button
            onClick={() => setActiveTab("manager")}
            style={{
              padding: "10px 28px",
              borderRadius: "8px 0px 0px 8px",
              border: activeTab === "manager" ? `2px solid var(--primary-color)` : "1px solid #e2e8f0",
              background: activeTab === "manager" ? "var(--primary-color)" : "#fff",
              color: activeTab === "manager" ? "#fff" : "var(--primary-color)",
              fontWeight: 600,
              fontSize: "1rem",
              fontFamily: "var(--font-primary)",
              letterSpacing: "0.025em",
              cursor: "pointer",
              transition: "all 0.2s ease",
              outline: "none",
            }}
          >
            {role === "manager" ? "Manager View" : "Member View"}
          </button>
          <button
            onClick={() => setActiveTab("pipeline")}
            style={{
              padding: "10px 28px",
              borderRadius: "0 8px 8px 0",
              border: activeTab === "pipeline" ? `2px solid var(--primary-color)` : "1px solid #e2e8f0",
              background: activeTab === "pipeline" ? "var(--primary-color)" : "#fff",
              color: activeTab === "pipeline" ? "#fff" : "var(--primary-color)",
              fontWeight: 600,
              fontSize: "1rem",
              fontFamily: "var(--font-primary)",
              letterSpacing: "0.025em",
              cursor: "pointer",
              transition: "all 0.2s ease",
              outline: "none",
            }}
          >
            Pipeline View
          </button>
        </div>
        <p style={{ fontSize: "1rem", color: "#5c6b89", fontWeight: 500, marginBottom: 24, marginTop: 8 }}>
          {activeTab === "pipeline"
            ? "Manage your widgets and track your pipeline"
            : (role === "manager" ? "Manager view: See team/user details here" : "Member view: See your details here")}
        </p>

        {/* Notification Bell (top-right) */}
        <div style={{ position: "absolute", top: 12, right: 0, display: "flex", alignItems: "center", gap: 12 }}>
          <button
            onClick={() => { setUnreadCount(0); navigate('/home/notifications'); }}
            aria-label="Notifications"
            style={{ position: "relative", background: "transparent", border: "none", cursor: "pointer", padding: 8, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#333" }}
            onMouseEnter={(e) => e.currentTarget.style.background = "#f5f5f5"}
            onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
          >
                <FiBell style={{ fontSize: 40 }} />
            {unread > 0 && (
              <span style={{
                position: "absolute",
                top: 4,
                right: 4,
                minWidth: 18,
                height: 18,
                background: "#e53e3e",
                color: "#fff",
                borderRadius: 12,
                boxShadow: "0 0 0 3px rgba(229,62,62,0.08)",
                padding: "0 6px",
                fontSize: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 600,
              
              }}>
                {unread}
              </span>
            )}
          </button>
        </div>

        {activeTab !== "manager" && <AddWidgetBtn />}
      </div>

      {/* Only show pipeline widgets if on pipeline tab */}
      {activeTab === "pipeline" && (
        <>
          {selectedWidgets.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(500px, 100%), 1fr))", gap: 24, marginBottom: 32 }}>
              {selectedWidgets.map((key) => {
                const WidgetComponent = WIDGETS.find((w) => w.key === key).component;
                return (
                  <div
                    key={key}
                    style={{
                      position: "relative",
                      background: "#fff",
                      borderRadius: 12,
                      boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)",
                      padding: 28,
                      minHeight: 120,
                      transition: "box-shadow 0.2s ease",
                      border: "1px solid #e8eaed",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12), 0 2px 4px rgba(0,0,0,0.08)"}
                    onMouseLeave={(e) => e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)"}
                  >
                    <button
                      onClick={() => handleRemoveWidget(key)}
                      style={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        background: "#f5f5f5",
                        border: "none",
                        color: "#666",
                        fontWeight: "bold",
                        fontSize: 20,
                        cursor: "pointer",
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#e53e3e";
                        e.currentTarget.style.color = "#fff";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "#f5f5f5";
                        e.currentTarget.style.color = "#666";
                      }}
                      title="Remove widget"
                    >
                      ×
                    </button>
                    <WidgetComponent />
                  </div>
                );
              })}
            </div>
          )}
          <KanbanBoard />
        </>
      )}

      {/* Manager/Member view tab content */}
      {activeTab === "manager" && (
        <div style={{
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)",
          padding: 32,
          minHeight: 900,
          border: "1px solid #e8eaed",
          marginBottom: 32,
        }}>
          {/* Replace below with your actual manager/member dashboard component */}
          {role === "manager" && <ManagerDashboard />}
          {role === "member" && <UserDetails id={userId} />}
          {!role && (
            <div>User role not found.</div>
          )}
        </div>
      )}
    </div>
  );
}